import React, { useEffect, useState } from 'react';
import InvoiceTable from '../../../components/tables/InvoiceTable';
import Button from '../../../components/common/Button';
import { useInvoiceApi } from '../../../hooks/useInvoiceApi';
import { useNetcashBatchApi } from '../../../hooks/useNetcashBatchApi';
import { confirmAction, showAlert } from '../../../utils/sweetAlertUtils';
import { isInvoiceSelectableForNetcash } from '../../../utils/invoiceEligibility';
import { InvoiceBatchDTO, InvoiceDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { FileViewer } from '../../../components/common/FileViewer';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { getApiErrorMessage, isClientError } from '../../../utils/apiErrorUtils';
import { describeNetcashAddResult } from '../../../utils/netcashAddSummary';
import AddToNetcashBatchDrawer, { NetcashBatchTarget } from '../../../components/drawer/AddToNetcashBatchDrawer';

interface InvoiceBatchDetailPanelProps {
  batchId: string;
  batch: InvoiceBatchDTO | null;
  isDebitOrderPayment: boolean;
  filterMemberId: string;
  awaitingFilterMember: boolean;
}

const InvoiceBatchDetailPanel: React.FC<InvoiceBatchDetailPanelProps> = ({
  batchId,
  batch,
  isDebitOrderPayment,
  filterMemberId,
  awaitingFilterMember,
}) => {
  const { search, remove, extract, getInvoiceSasUrl, revert, isLoading } = useInvoiceApi();
  const { addInvoices, createDraft, getInvoiceAvailability, isLoading: isAddingToNetcashBatch } = useNetcashBatchApi();
  const [netcashBlocked, setNetcashBlocked] = useState<Record<string, string>>({});
  const [isNetcashDrawerOpen, setIsNetcashDrawerOpen] = useState(false);
  const [items, setItems] = useState<InvoiceDTO[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [sortBy, setSortBy] = useState<string>('SalesOrderNumber');
  const [sortDescending, setSortDescending] = useState<boolean>(true);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerFileName, setViewerFileName] = useState('');

  const navigate = useNavigate();

  const fetchItems = async (page: number, size: number, filters?: any) => {
    if (!batch?.batchNo) return;

    try {
      const response = await search({
        filters: {
          invoiceBatchId: batchId,
          isDebitOrderPayment,
          ...(filterMemberId ? { memberId: filterMemberId } : {}),
          ...filters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      const results = response?.data?.results || [];
      setItems(results);
      setTotalRecords(response?.data?.totalCount || 0);
      await fetchNetcashAvailability(results);
    } catch (error) {
      console.error('Error fetching Invoices:', error);
    }
  };

  // Only the Debit Order tab can add to Netcash batches, so only it needs to know which invoices
  // are already committed to one.
  const fetchNetcashAvailability = async (rows: InvoiceDTO[]) => {
    if (!isDebitOrderPayment) {
      setNetcashBlocked({});
      return;
    }

    const ids = rows.map((r) => r.id).filter((id): id is string => !!id);
    if (ids.length === 0) {
      setNetcashBlocked({});
      return;
    }

    try {
      const response = await getInvoiceAvailability(ids);
      const map: Record<string, string> = {};
      (response?.data?.committed ?? []).forEach((c) => {
        if (c.invoiceId) map[c.invoiceId.toLowerCase()] = c.label ?? 'In a Netcash batch';
      });
      setNetcashBlocked(map);
    } catch (error) {
      // Not fatal: the server still refuses to double-add, the tab just can't pre-mark them
      console.error('Error checking Netcash availability:', error);
      setNetcashBlocked({});
    }
  };

  useEffect(() => {
    if (!batch) return;
    if (awaitingFilterMember) return;

    fetchItems(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, batch, isDebitOrderPayment, filterMemberId, awaitingFilterMember, sortBy, sortDescending]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMemberId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [items]);

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableIds = items
        .filter((item) => isInvoiceSelectableForNetcash(item) && !(item.id && netcashBlocked[item.id.toLowerCase()]))
        .map(item => item.id)
        .filter((id): id is string => !!id);
      setSelectedIds(new Set(selectableIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleAddSelectedToNetcashBatch = () => {
    if (selectedIds.size === 0) return;
    setIsNetcashDrawerOpen(true);
  };

  const handleConfirmAddToNetcashBatch = async (target: NetcashBatchTarget) => {
    try {
      let netcashBatchId = target.batchId;
      if (!netcashBatchId) {
        const created = await createDraft(target.year, target.month);
        netcashBatchId = created?.data?.id;
      }
      if (!netcashBatchId) throw new Error('The draft Netcash batch could not be created.');

      const response = await addInvoices(netcashBatchId, Array.from(selectedIds));
      const result = response?.data;
      const monthLabel = `${target.month}/${target.year}`;

      setIsNetcashDrawerOpen(false);
      await showAlert({
        title: (result?.addedCount ?? 0) === 0 ? 'Nothing Added' : 'Added to Netcash Batch',
        text:
          `${describeNetcashAddResult(result)} ` +
          `That draft (${monthLabel}) now has ${result?.batch?.totalCount ?? 0} invoice(s) totalling R${(result?.batch?.totalAmount ?? 0).toFixed(2)}. ` +
          'Choose the collection date and push it from the Netcash Batches tab when ready.',
        icon: (result?.addedCount ?? 0) === 0 ? 'warning' : 'success',
        confirmButtonText: 'OK',
      });
      setSelectedIds(new Set());
      await fetchItems(currentPage, pageSize);
    } catch (error: any) {
      console.error('Error adding selected invoices to Netcash batch:', error);
      await showAlert({
        title: isClientError(error) ? 'Nothing Added' : 'Error',
        text: getApiErrorMessage(error, 'Failed to add selected invoices to the Netcash batch. Please try again.'),
        icon: isClientError(error) ? 'warning' : 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(sortKey);
      setSortDescending(false);
    }
  };

  const handleDelete = async (dto: InvoiceDTO) => {
    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting Invoice:', error);
    }
  };

  const handleExport = async () => {
    if (!batch?.batchNo) return;

    if (awaitingFilterMember) {
      await showAlert({
        title: 'Filter not ready',
        text: 'Please wait for the member filter to load before exporting.',
        icon: 'info',
      });
      return;
    }

    try {
      const response = await extract({
        filters: {
          invoiceBatchId: batchId,
          isDebitOrderPayment,
          ...(filterMemberId ? { memberId: filterMemberId } : {}),
        },
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Invoices:', error);
    }
  };

  const handleViewMember = (invoice: InvoiceDTO) => {
    if (invoice.memberId) {
      navigate(`/admin/members/${invoice.memberId}`);
    }
  };

  const handleLoginAsMember = async (invoice: InvoiceDTO) => {
    if (!invoice.member?.userID) return;

    try {
      window.open(`/admin/impersonate/${invoice.memberId}`, '_blank');
    } catch (error) {
      console.error('Error opening impersonation tab', error);
    }
  };

  const handleViewDocument = async (invoice: InvoiceDTO) => {
    try {
      const response = await getInvoiceSasUrl(invoice.id ?? '');
      const fileName = invoice.invoiceNumber
        ? `Invoice_${invoice.invoiceNumber}.pdf`
        : `PaymentNotice_${invoice.salesOrderNumber}.pdf`;

      if (response && response.success && response.data) {
        setViewerUrl(response.data);
        setViewerFileName(fileName);
        setIsViewerOpen(true);
      }
    } catch (error) {
      console.error('Error loading invoice document:', error);
    }
  };

  const handleRevertInvoice = async (invoice: InvoiceDTO) => {
    if (!invoice.id) return;

    try {
      const response = await revert(invoice.id);

      if (response && response.success) {
        await showAlert({
          title: 'Success',
          text: 'Invoice has been reverted successfully',
          icon: 'success',
        });

        await fetchItems(currentPage, pageSize);
      }
    } catch (error: any) {
      console.error('Error reverting invoice:', error);
      await showAlert({
        title: 'Error',
        text: error.message || 'Failed to revert invoice',
        icon: 'error',
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Total Records: {totalRecords}
        </p>
        <div className="flex space-x-2">
          {isDebitOrderPayment && selectedIds.size > 0 && (
            <Button
              type="button"
              variant="primary"
              onClick={handleAddSelectedToNetcashBatch}
              isLoading={isAddingToNetcashBatch}
              disabled={isAddingToNetcashBatch}
            >
              Add {selectedIds.size} Selected to Netcash Batch
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={handleExport}>
            Export
          </Button>
        </div>
      </div>

      <div className='mb-4'>
        <GeneralSearch onSubmit={handleSearch} searchFilters={searchFilters} setSearchFilters={setSearchFilters} />
      </div>
      <InvoiceTable
        data={items}
        showEditForPaymentAction={true}
        batchMonth={batch?.month}
        batchYear={batch?.year}
        onRefresh={async () => {
          await fetchItems(currentPage, pageSize);
        }}
        handleViewMember={handleViewMember}
        handleLoginAsMember={handleLoginAsMember}
        handleViewDocument={handleViewDocument}
        handleRevertInvoice={handleRevertInvoice}
        handleDelete={handleDelete}
        currentPage={currentPage}
        totalPages={Math.ceil(totalRecords / pageSize)}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={handleSort}
        {...(isDebitOrderPayment
          ? { selectedIds, onSelectRow: handleSelectRow, onSelectAll: handleSelectAll, netcashBlocked }
          : {})}
      />

      <AddToNetcashBatchDrawer
        isOpen={isNetcashDrawerOpen}
        onClose={() => setIsNetcashDrawerOpen(false)}
        subject={`${selectedIds.size} selected invoice${selectedIds.size === 1 ? '' : 's'}`}
        defaultYear={batch?.year ?? new Date().getFullYear()}
        defaultMonth={batch?.month ?? new Date().getMonth() + 1}
        isSubmitting={isAddingToNetcashBatch}
        onConfirm={handleConfirmAddToNetcashBatch}
      />

      {isViewerOpen && (
        <FileViewer
          fileName={viewerFileName}
          sasUrl={viewerUrl}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </div>
  );
};

export default InvoiceBatchDetailPanel;
